import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/suffa/SiteHeader";
import SiteFooter from "@/components/suffa/SiteFooter";
import { OrnamentDivider } from "@/components/suffa/Ornaments";
import { ProfilePreview } from "@/components/suffa/profile/StepsPart3";
import RegistrationPaymentCard from "@/components/suffa/profile/RegistrationPaymentCard";
import { INITIAL_DATA, STEPS } from "@/lib/profileForm";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import "@/pages/CreateProfile.css";
import "@/pages/Admin.css";

const TABS = [
  ["profile", "My Profile"],
  ["interests", "Interests"],
  ["discussions", "Discussions"],
  ["notifications", "Notifications"],
];

const StatusChip = ({ value }) => {
  const tone = { mutual: "ap-chip-green", approved: "ap-chip-gold", pending_admin_review: "ap-chip-amber", declined: "ap-chip-gray", rejected: "ap-chip-red" }[value] || "ap-chip-gray";
  const label = { mutual: "Mutual Interest", approved: "Sent — awaiting response", pending_admin_review: "Under Suffa review", declined: "Declined", rejected: "Not approved" }[value] || value;
  return <span className={`ap-chip ${tone}`}>{label}</span>;
};

const InterestsTab = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const load = () => api.get("/interests/mine").then(setData).catch((e) => toast.error(formatApiError(e)));
  useEffect(load, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/interests/${id}/${action}`);
      toast.success(action === "accept" ? t("Interest accepted — Suffa Matrimony will guide the next steps.") : t("Interest declined."));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <p className="cp-subtitle">{t("Loading…")}</p>;

  const Section = ({ title, items, actions }) => (
    <div className="ap-card" style={{ marginBottom: 16 }}>
      <h3 className="ap-card-title">{title}</h3>
      {items.length === 0 ? (
        <p className="ap-note">None yet, insha'Allah.</p>
      ) : (
        <table className="ap-table">
          <tbody>
            {items.map((i) => (
              <tr key={i.id}>
                <td>
                  <strong>{i.other ? `${i.other.id} — ${i.other.city}, ${i.other.country}` : "—"}</strong>
                  <br /><span className="ap-note">{i.other ? `${i.other.age} yrs · ${i.other.education} · ${i.other.profession}` : ""}</span>
                </td>
                <td><StatusChip value={i.status} /></td>
                {actions && i.status === "approved" && (
                  <td style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn btn-solid btn-sm" data-testid={`ap-interest-accept-${i.id}`} onClick={() => decide(i.id, "accept")}>Accept</button>
                    <button type="button" className="btn btn-outline btn-sm" data-testid={`ap-interest-decline-${i.id}`} onClick={() => decide(i.id, "decline")}>Decline</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <>
      <Section title="Received Interests" items={data.received} actions />
      <Section title="Sent Interests" items={data.sent} />
    </>
  );
};

const DiscussionsTab = () => {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/discussions/mine").then(setData).catch(() => setData({ discussions: [] }));
  useEffect(load, []);

  const send = async (id) => {
    try {
      await api.post(`/discussions/${id}/messages`, { message: msg });
      setMsg("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <p className="cp-subtitle">Loading…</p>;
  if (data.discussions.length === 0) {
    return (
      <div className="ap-card">
        <p className="ap-note">
          This is a supervised matrimonial discussion facilitated by Suffa Matrimony. It is intended only for serious
          matrimonial communication. A discussion is opened for your family when a mutual interest progresses, insha'Allah.
        </p>
      </div>
    );
  }
  return data.discussions.map((d) => (
    <div className="ap-card" style={{ marginBottom: 16 }} key={d.id}>
      <h3 className="ap-card-title">{d.title}</h3>
      <p className="ap-note" style={{ marginBottom: 10 }}>
        This is a supervised matrimonial discussion facilitated by Suffa Matrimony. It is intended only for serious
        matrimonial communication.
      </p>
      {d.messages.map((m, i) => (
        <div key={i} className={`ap-msg${m.sender_role === "admin" ? " ap-msg-admin" : ""}`}>
          <div className="ap-msg-meta">{m.sender_name}</div>
          {m.message}
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <input className="ap-input" data-testid={`ap-discussion-input-${d.id.slice(-4)}`} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a respectful message…" />
        <button type="button" className="btn btn-solid btn-sm" data-testid={`ap-discussion-send-${d.id.slice(-4)}`} onClick={() => send(d.id)}>Send</button>
      </div>
    </div>
  ));
};

const NotificationsTab = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/notifications").then(setData).catch(() => setData({ notifications: [], unread: 0 }));
  useEffect(load, []);

  if (!data) return <p className="cp-subtitle">Loading…</p>;
  return (
    <div className="ap-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 className="ap-card-title" style={{ margin: 0 }}>Notifications {data.unread > 0 && <span className="ap-chip ap-chip-amber">{data.unread} new</span>}</h3>
        {data.notifications.length > 0 && (
          <button type="button" className="btn btn-outline btn-sm" data-testid="ap-notif-mark-read" onClick={() => api.post("/notifications/read").then(load)}>Mark all read</button>
        )}
      </div>
      {data.notifications.length === 0 ? (
        <p className="ap-note">No notifications yet, insha'Allah.</p>
      ) : (
        data.notifications.map((n) => (
          <div key={n.id} className="ap-msg" style={{ borderColor: n.read ? undefined : "#C5A059" }} data-testid="ap-notification-item">
            <div className="ap-msg-meta">{new Date(n.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</div>
            <strong>{n.title}</strong>
            {n.body && <p style={{ margin: "4px 0 0", fontSize: 13.5 }}>{n.body}</p>}
          </div>
        ))
      )}
    </div>
  );
};

export default function MyProfile() {
  const { t } = useLanguage();
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined); // undefined = loading, null = none
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    document.title = "My Profile | Suffa Matrimony";
  }, []);

  useEffect(() => {
    if (!authReady || !user) return;
    (async () => {
      try {
        const d = await api.get("/profiles/me");
        setProfile(d.profile);
      } catch (e) {
        setProfile(null);
      }
    })();
  }, [authReady, user]);

  const stepTitleMap = useMemo(
    () => Object.fromEntries(STEPS.map((s) => [s.n, s.title])),
    [],
  );

  if (!authReady) {
    return (
      <div className="suffa-page">
        <SiteHeader />
        <main className="cp-main">
          <div className="cp-container" style={{ padding: "100px 0", textAlign: "center" }}>
            <p className="cp-subtitle" data-testid="my-profile-loading">{t("Loading…")}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="suffa-page">
        <SiteHeader />
        <main className="cp-main">
          <div className="cp-container" style={{ padding: "60px 0 120px" }}>
            <section
              className="cp-card"
              style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}
              data-testid="my-profile-login-required"
            >
              <OrnamentDivider />
              <h2 className="cp-step-title">{t("Login Required")}</h2>
              <p className="cp-subtitle" style={{ marginBottom: 24 }}>
                {t(
                  "Your matrimonial profile is saved securely with your Suffa Matrimony account. Please login or create an account to continue.",
                )}
              </p>
              <div className="cp-actions" style={{ justifyContent: "center" }}>
                <a href="/login" className="btn btn-outline btn-lg" data-testid="my-profile-gate-login">
                  {t("Login")}
                </a>
                <a href="/register" className="btn btn-solid btn-lg" data-testid="my-profile-gate-register">
                  {t("Register")}
                </a>
              </div>
            </section>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="suffa-page">
      <SiteHeader />
      <main className="cp-main">
        <section className="cp-masthead">
          <div className="cp-container cp-masthead-inner">
            <OrnamentDivider />
            <h1 className="cp-title" data-testid="my-profile-title">
              {t("My Profile")}
            </h1>
            <p className="cp-subtitle" data-testid="my-profile-subtitle">
              {t("Your matrimonial profile — securely saved with your Suffa Matrimony account.")}
            </p>
          </div>
        </section>

        <div className="cp-container">
          <div className="ap-tabs" style={{ marginBottom: 18 }} data-testid="my-profile-tabs">
            {TABS.map(([key, label]) => (
              <button key={key} type="button" className={`ap-tab${tab === key ? " is-active" : ""}`} data-testid={`my-profile-tab-${key}`} onClick={() => setTab(key)}>
                {t(label)}
              </button>
            ))}
          </div>

          {tab === "interests" && <InterestsTab />}
          {tab === "discussions" && <DiscussionsTab />}
          {tab === "notifications" && <NotificationsTab />}

          {tab === "profile" && (
            <>
              {profile === undefined && (
                <p className="cp-subtitle" data-testid="my-profile-loading">{t("Loading…")}</p>
              )}

              {profile === null && (
                <section className="cp-card" style={{ textAlign: "center" }} data-testid="my-profile-empty">
                  <h2 className="cp-step-title">{t("You haven't completed your profile yet.")}</h2>
                  <p className="cp-subtitle" style={{ marginBottom: 24 }}>
                    {t("Create Your Profile")}
                  </p>
                  <div className="cp-actions" style={{ justifyContent: "center" }}>
                    <a href="/create-profile" className="btn btn-solid btn-lg" data-testid="my-profile-create-cta">
                      {t("Create Your Profile")}
                    </a>
                  </div>
                </section>
              )}

              {profile && (
                <>
                  <RegistrationPaymentCard />
                  <div className="cp-draft-note" data-testid="my-profile-meta">
                    <span>
                      {t("Profile ID")}: <strong>{profile.profileId}</strong> ·{" "}
                      <span className="cp-private-chip" data-testid="my-profile-status">
                        {profile.approved ? t("Active") : t("Pending review")}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-solid btn-sm"
                      data-testid="my-profile-edit-btn"
                      onClick={() => navigate("/create-profile")}
                    >
                      {t("Edit Profile")}
                    </button>
                  </div>
                  <section className="cp-card">
                    <ProfilePreview
                      data={{ ...INITIAL_DATA, ...profile.data }}
                      onEdit={() => navigate("/create-profile")}
                      stepTitleMap={stepTitleMap}
                    />
                  </section>
                </>
              )}
            </>
          )}
        </div>
        <div className="cp-footer-space" />
      </main>
      <SiteFooter />
    </div>
  );
}
